import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { ClientConfirmation, ConfirmationStatus, Project, TimeEntry, Material } from '../types';
import { calculateLaborCost, calculateMaterialsCost, calculateProjectTotal, calculateWorkingHours } from '../utils/calculationUtils';

interface ClientConfirmationsContextType {
  confirmations: ClientConfirmation[];
  loading: boolean;
  getConfirmationsByProject: (projectId: string) => ClientConfirmation[];
  getConfirmationByToken: (token: string) => Promise<ClientConfirmation | undefined>;
  createConfirmation: (projectId: string, clientEmail: string, project: Project) => Promise<ClientConfirmation>;
  updateConfirmation: (confirmation: ClientConfirmation) => Promise<void>;
  deleteConfirmation: (id: string) => Promise<void>;
  fetchConfirmations: () => Promise<void>;
}

const ClientConfirmationsContext = createContext<ClientConfirmationsContextType | undefined>(undefined);

export const useClientConfirmations = () => {
  const context = useContext(ClientConfirmationsContext);
  if (!context) {
    throw new Error('useClientConfirmations must be used within a ClientConfirmationsProvider');
  }
  return context;
};

export const ClientConfirmationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();
  const [confirmations, setConfirmations] = useState<ClientConfirmation[]>([]);
  const [loading, setLoading] = useState(true);

  // Define fetchConfirmations BEFORE using it in useEffect hooks
  const fetchConfirmations = useCallback(async () => {
    if (!currentOrganization) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('client_confirmations')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching confirmations:', error);
        return;
      }

      if (data) {
        const transformedConfirmations: ClientConfirmation[] = data.map(conf => ({
          id: conf.id,
          projectId: conf.project_id,
          clientEmail: conf.client_email,
          status: conf.status as ConfirmationStatus,
          confirmationData: conf.confirmation_data as ClientConfirmation['confirmationData'],
          sentAt: conf.sent_at,
          confirmedAt: conf.confirmed_at,
          clientNotes: conf.client_notes,
          uniqueToken: conf.unique_token,
          createdAt: conf.created_at
        }));

        setConfirmations(transformedConfirmations);
      }
    } catch (error) {
      console.error('Error in fetchConfirmations:', error);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization]);

  // Fetch confirmations from database when authenticated and org is selected
  useEffect(() => {
    if (isAuthenticated && currentOrganization) {
      fetchConfirmations();
    }
  }, [isAuthenticated, currentOrganization?.id, fetchConfirmations]);

  // Set up real-time subscription to detect when confirmations are updated (client confirms/rejects)
  useEffect(() => {
    if (!isAuthenticated || !currentOrganization) return;

    console.log('🔔 Setting up real-time subscription for client_confirmations');

    const channel = supabase
      .channel(`client_confirmations:${currentOrganization.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE', // Listen to UPDATE events (when client confirms/rejects)
          schema: 'public',
          table: 'client_confirmations',
          filter: `organization_id=eq.${currentOrganization.id}`,
        },
        (payload) => {
          console.log('📢 Real-time confirmation update received:', payload);
          console.log('📊 Updated confirmation data:', payload.new);
          // Refresh confirmations when a confirmation is updated
          fetchConfirmations();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT', // Also listen to INSERT for new confirmations
          schema: 'public',
          table: 'client_confirmations',
          filter: `organization_id=eq.${currentOrganization.id}`,
        },
        (payload) => {
          console.log('📢 Real-time confirmation insert received:', payload);
          // Refresh confirmations when a new confirmation is created
          fetchConfirmations();
        }
      )
      .subscribe((status) => {
        console.log('📡 Client confirmations subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to client_confirmations changes');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Error subscribing to client_confirmations changes');
        }
      });

    // Cleanup subscription on unmount
    return () => {
      console.log('🔕 Unsubscribing from client_confirmations channel');
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, currentOrganization?.id, fetchConfirmations]);

  const getConfirmationsByProject = (projectId: string) => {
    return confirmations.filter(confirmation => confirmation.projectId === projectId);
  };

  const getConfirmationByToken = async (token: string): Promise<ClientConfirmation | undefined> => {
    // First check local state
    let confirmation = confirmations.find(confirmation => confirmation.uniqueToken === token);
    
    // If not found in local state, try fetching from database (for public access)
    if (!confirmation) {
      try {
        const { data, error } = await supabase
          .from('client_confirmations')
          .select('*')
          .eq('unique_token', token)
          .maybeSingle();
        
        if (!error && data) {
          confirmation = {
            id: data.id,
            projectId: data.project_id,
            clientEmail: data.client_email,
            status: data.status as ConfirmationStatus,
            confirmationData: data.confirmation_data as ClientConfirmation['confirmationData'],
            sentAt: data.sent_at,
            confirmedAt: data.confirmed_at,
            clientNotes: data.client_notes,
            uniqueToken: data.unique_token,
            createdAt: data.created_at
          };
          
          // Add to local state if found
          setConfirmations(prev => {
            if (!prev.find(c => c.id === confirmation!.id)) {
              return [...prev, confirmation!];
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Error fetching confirmation by token:', error);
      }
    }
    
    return confirmation;
  };

  const createConfirmation = async (projectId: string, clientEmail: string, project: Project): Promise<ClientConfirmation> => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }

    // Generate a unique token
    const uniqueToken = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    
    // Calculate total hours
    const totalHours = calculateWorkingHours(project.timeEntries);
    
    // Create confirmation data snapshot
    const confirmationData = {
      project: {
        title: project.title,
        description: project.description,
        client: { ...project.client }
      },
      timeEntries: project.timeEntries,
      materials: project.materials,
      totalHours,
      totalCost: calculateProjectTotal(project)
    };
    
    try {
      // Get project organization_id
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('organization_id')
        .eq('id', projectId)
        .single();
      
      if (projectError || !projectData) {
        throw new Error('Project not found');
      }

      const { data, error } = await supabase
        .from('client_confirmations')
        .insert({
          project_id: projectId,
          client_email: clientEmail,
          status: 'pending',
          confirmation_data: confirmationData,
          unique_token: uniqueToken,
          organization_id: projectData.organization_id,
          sent_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating confirmation:', error);
        throw error;
      }

      const newConfirmation: ClientConfirmation = {
        id: data.id,
        projectId: data.project_id,
        clientEmail: data.client_email,
        status: data.status as ConfirmationStatus,
        confirmationData: data.confirmation_data as ClientConfirmation['confirmationData'],
        sentAt: data.sent_at,
        confirmedAt: data.confirmed_at,
        clientNotes: data.client_notes,
        uniqueToken: data.unique_token,
        createdAt: data.created_at
      };

      setConfirmations(prev => [...prev, newConfirmation]);
      return newConfirmation;
    } catch (error) {
      console.error('Error in createConfirmation:', error);
      throw error;
    }
  };

  const updateConfirmation = async (updatedConfirmation: ClientConfirmation) => {
    try {
      console.log('🔄 Updating confirmation in database:', {
        id: updatedConfirmation.id,
        status: updatedConfirmation.status,
        confirmedAt: updatedConfirmation.confirmedAt
      });

      const { data, error } = await supabase
        .from('client_confirmations')
        .update({
          status: updatedConfirmation.status,
          confirmed_at: updatedConfirmation.confirmedAt || null,
          client_notes: updatedConfirmation.clientNotes || null
        })
        .eq('id', updatedConfirmation.id)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating confirmation:', error);
        throw error;
      }

      console.log('✅ Confirmation updated in database:', data);

      // Transform the response to match our ClientConfirmation type
      const transformedConfirmation: ClientConfirmation = {
        id: data.id,
        projectId: data.project_id,
        clientEmail: data.client_email,
        status: data.status as ConfirmationStatus,
        confirmationData: data.confirmation_data as ClientConfirmation['confirmationData'],
        sentAt: data.sent_at,
        confirmedAt: data.confirmed_at,
        clientNotes: data.client_notes,
        uniqueToken: data.unique_token,
        createdAt: data.created_at
      };

      // Update local state immediately with the data from the database (most accurate)
      setConfirmations(prev => 
        prev.map(confirmation => 
          confirmation.id === updatedConfirmation.id ? transformedConfirmation : confirmation
        )
      );

      // Also trigger a refresh after a short delay to ensure real-time subscription updates are captured
      // This acts as a fallback if the real-time subscription has any issues
      // Use multiple delayed refreshes to ensure we catch the update
      setTimeout(() => {
        console.log('🔄 Delayed refresh after update (1s)');
        fetchConfirmations();
      }, 1000);
      setTimeout(() => {
        console.log('🔄 Delayed refresh after update (3s)');
        fetchConfirmations();
      }, 3000);
    } catch (error) {
      console.error('Error in updateConfirmation:', error);
      throw error;
    }
  };

  const deleteConfirmation = async (id: string) => {
    try {
      const { error } = await supabase
        .from('client_confirmations')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting confirmation:', error);
        throw error;
      }

      setConfirmations(prev => prev.filter(confirmation => confirmation.id !== id));
    } catch (error) {
      console.error('Error in deleteConfirmation:', error);
      throw error;
    }
  };

  return (
    <ClientConfirmationsContext.Provider value={{
      confirmations,
      loading,
      getConfirmationsByProject,
      getConfirmationByToken,
      createConfirmation,
      updateConfirmation,
      deleteConfirmation,
      fetchConfirmations
    }}>
      {children}
    </ClientConfirmationsContext.Provider>
  );
};